/* ===========================================================================
   PulseTech — Claude Design Layer v2 (behaviour)
   Self-contained: no external libraries, no network requests.
   Everything is progressive enhancement — if this file fails to parse the
   page renders exactly as the stock theme does.
   =========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE_POINTER = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var DESIGN_MODE = !!(window.Shopify && window.Shopify.designMode);
  var body = document.body;

  function el(tag, cls, parent) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    (parent || body).appendChild(n);
    return n;
  }

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* =========================================================================
     1. AURORA — WebGL flowing plasma, with an animated CSS fallback.
     Rendered at reduced resolution and upscaled: cheap, and the smoothing
     actually helps the look.
     ========================================================================= */
  function initAurora() {
    if (REDUCED) return;

    var canvas = el('canvas', 'cfx-aurora');
    var gl = null;
    try {
      gl = canvas.getContext('webgl', { alpha: true, antialias: false, depth: false, powerPreference: 'low-power' })
        || canvas.getContext('experimental-webgl');
    } catch (e) { gl = null; }

    if (!gl) {
      canvas.parentNode.removeChild(canvas);
      el('div', 'cfx-aurora-fallback');
      return;
    }

    var VERT = 'attribute vec2 p;void main(){gl_Position=vec4(p,0.0,1.0);}';

    var FRAG = [
      'precision highp float;',
      'uniform vec2 u_res;',
      'uniform float u_time;',
      'uniform vec2 u_mouse;',
      'vec2 hash2(vec2 p){p=vec2(dot(p,vec2(127.1,311.7)),dot(p,vec2(269.5,183.3)));',
      ' return -1.0+2.0*fract(sin(p)*43758.5453123);}',
      'float noise(vec2 p){const float K1=0.366025404;const float K2=0.211324865;',
      ' vec2 i=floor(p+(p.x+p.y)*K1);vec2 a=p-i+(i.x+i.y)*K2;',
      ' float m=step(a.y,a.x);vec2 o=vec2(m,1.0-m);',
      ' vec2 b=a-o+K2;vec2 c=a-1.0+2.0*K2;',
      ' vec3 h=max(0.5-vec3(dot(a,a),dot(b,b),dot(c,c)),0.0);',
      ' vec3 n=h*h*h*h*vec3(dot(a,hash2(i)),dot(b,hash2(i+o)),dot(c,hash2(i+1.0)));',
      ' return dot(n,vec3(70.0));}',
      // 4 octaves only: fewer, larger forms read better once blurred
      'float fbm(vec2 p){float v=0.0;float a=0.5;',
      ' for(int i=0;i<4;i++){v+=a*noise(p);p*=2.03;a*=0.5;}return v;}',
      'void main(){',
      ' vec2 uv=gl_FragCoord.xy/u_res.xy;',
      ' vec2 st=uv;st.x*=u_res.x/u_res.y;',
      ' float t=u_time*0.05;',
      // domain warp for the flowing ribbon look
      ' vec2 q=vec2(fbm(st*0.85+vec2(0.0,t)),fbm(st*0.85+vec2(5.2,1.3-t)));',
      ' vec2 r=vec2(fbm(st*1.05+3.0*q+vec2(1.7,9.2)+t*0.7),',
      '             fbm(st*1.05+3.0*q+vec2(8.3,2.8)-t*0.6));',
      ' float f=fbm(st*0.8+3.0*r);',
      ' float m=length(st-u_mouse*vec2(u_res.x/u_res.y,1.0));',
      ' f+=0.28*exp(-m*2.4);',
      // palette: navy -> purple -> violet -> cyan -> magenta
      ' vec3 navy=vec3(0.043,0.078,0.184);',
      ' vec3 purple=vec3(0.510,0.208,0.592);',
      ' vec3 violet=vec3(0.486,0.361,1.000);',
      ' vec3 cyan=vec3(0.133,0.886,1.000);',
      ' vec3 magenta=vec3(0.941,0.424,0.925);',
      ' vec3 col=navy;',
      ' col=mix(col,purple,clamp(f*1.5+0.35,0.0,1.0));',
      ' col=mix(col,violet,clamp(length(q)*1.15,0.0,1.0));',
      ' col=mix(col,cyan,clamp(r.x*r.x*1.9,0.0,1.0));',
      ' col=mix(col,magenta,clamp(r.y*r.y*1.5,0.0,1.0));',
      ' col+=0.10*clamp(f*f*2.4,0.0,1.0);',
      // vignette so page edges stay calm
      ' float vig=smoothstep(1.25,0.25,length(uv-0.5));',
      ' col*=0.55+0.45*vig;',
      ' gl_FragColor=vec4(col,0.94);',
      '}'
    ].join('\n');

    function compile(type, src) {
      var s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { return null; }
      return s;
    }

    var vs = compile(gl.VERTEX_SHADER, VERT);
    var fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) {
      canvas.parentNode.removeChild(canvas);
      el('div', 'cfx-aurora-fallback');
      return;
    }

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      canvas.parentNode.removeChild(canvas);
      el('div', 'cfx-aurora-fallback');
      return;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var loc = gl.getAttribLocation(prog, 'p');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    var uRes = gl.getUniformLocation(prog, 'u_res');
    var uTime = gl.getUniformLocation(prog, 'u_time');
    var uMouse = gl.getUniformLocation(prog, 'u_mouse');

    var SCALE = 0.26; // render small, upscale + CSS-blur into a soft field
    function resize() {
      var w = Math.max(1, Math.floor(window.innerWidth * SCALE));
      var h = Math.max(1, Math.floor(window.innerHeight * SCALE));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w; canvas.height = h;
        gl.viewport(0, 0, w, h);
      }
    }
    resize();
    window.addEventListener('resize', resize, { passive: true });

    var mx = 0.5, my = 0.5, tmx = 0.5, tmy = 0.5;
    if (FINE_POINTER) {
      window.addEventListener('pointermove', function (e) {
        tmx = e.clientX / window.innerWidth;
        tmy = 1 - e.clientY / window.innerHeight;
      }, { passive: true });
    }

    var start = performance.now();
    var visible = true;
    document.addEventListener('visibilitychange', function () { visible = !document.hidden; });

    function frame(now) {
      requestAnimationFrame(frame);
      if (!visible) return;
      mx += (tmx - mx) * 0.04;
      my += (tmy - my) * 0.04;
      gl.uniform2f(uRes, canvas.width, canvas.height);
      gl.uniform1f(uTime, (now - start) / 1000);
      gl.uniform2f(uMouse, mx, my);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }
    requestAnimationFrame(frame);
  }

  /* =========================================================================
     2. MOTES — drifting light particles over the aurora
     ========================================================================= */
  function initMotes() {
    if (REDUCED) return;
    var canvas = el('canvas', 'cfx-motes');
    var ctx = canvas.getContext('2d');
    if (!ctx) return;

    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var parts = [];
    var COLORS = ['34,226,255', '124,92,255', '240,108,236'];

    function size() {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      var target = clamp(Math.round(window.innerWidth / 26), 22, 60);
      parts = [];
      for (var i = 0; i < target; i++) {
        parts.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: (Math.random() * 2.2 + 0.6) * dpr,
          vx: (Math.random() - 0.5) * 0.22 * dpr,
          vy: -(Math.random() * 0.32 + 0.06) * dpr,
          a: Math.random() * 0.5 + 0.2,
          c: COLORS[(Math.random() * COLORS.length) | 0],
          ph: Math.random() * 6.28
        });
      }
    }
    size();
    window.addEventListener('resize', size, { passive: true });

    var visible = true;
    document.addEventListener('visibilitychange', function () { visible = !document.hidden; });

    function draw(now) {
      requestAnimationFrame(draw);
      if (!visible) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i];
        p.x += p.vx + Math.sin(now / 2600 + p.ph) * 0.16 * dpr;
        p.y += p.vy;
        if (p.y < -20) { p.y = canvas.height + 20; p.x = Math.random() * canvas.width; }
        if (p.x < -20) p.x = canvas.width + 20;
        if (p.x > canvas.width + 20) p.x = -20;
        var tw = p.a * (0.6 + 0.4 * Math.sin(now / 900 + p.ph));
        var g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 6);
        g.addColorStop(0, 'rgba(' + p.c + ',' + tw.toFixed(3) + ')');
        g.addColorStop(1, 'rgba(' + p.c + ',0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 6, 0, 6.2832);
        ctx.fill();
      }
    }
    requestAnimationFrame(draw);
  }

  /* =========================================================================
     3. SCROLL PROGRESS + header state
     ========================================================================= */
  function initScrollChrome() {
    var bar = el('div', 'cfx-progress');
    var ticking = false;
    function update() {
      ticking = false;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? window.scrollY / h : 0;
      bar.style.transform = 'scaleX(' + clamp(p, 0, 1) + ')';
      body.classList.toggle('cfx-scrolled', window.scrollY > 40);
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* =========================================================================
     5. SECTION DEPTH — make opaque section backgrounds translucent so the
     aurora reads through, and lay an animated colour wash over each one.
     ========================================================================= */
  function parseRGB(str) {
    var m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,/\s]+([\d.]+))?/i.exec(str || '');
    if (!m) return null;
    return { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : +m[4] };
  }

  function initSections() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var sections = main.querySelectorAll(':scope > .shopify-section, :scope > [id^="shopify-section"]');

    Array.prototype.forEach.call(sections, function (sec) {
      if (sec.classList.contains('cfx-section')) return;
      sec.classList.add('cfx-section');

      if (!REDUCED) {
        var glow = document.createElement('div');
        glow.className = 'cfx-section-glow';
        glow.setAttribute('aria-hidden', 'true');
        sec.insertBefore(glow, sec.firstChild);
      }

      // Soften opaque backgrounds on the section's own full-width wrappers.
      var secWidth = sec.offsetWidth || 1;
      var candidates = sec.querySelectorAll(':scope > *, :scope > * > *');
      Array.prototype.forEach.call(candidates, function (node) {
        if (node.classList.contains('cfx-section-glow')) return;
        if (node.offsetWidth < secWidth * 0.88) return;
        var bg = parseRGB(getComputedStyle(node).backgroundColor);
        if (!bg || bg.a < 0.95) return;
        node.style.backgroundColor = 'rgba(' + bg.r + ',' + bg.g + ',' + bg.b + ',0.74)';
        node.style.backdropFilter = 'blur(6px) saturate(150%)';
        node.style.webkitBackdropFilter = 'blur(6px) saturate(150%)';
      });
    });
  }

  /* =========================================================================
     6. HOLOGRAPHIC HEADINGS + per-character hero lift
     ========================================================================= */
  function initHeadings() {
    var heads = document.querySelectorAll(
      '.banner__heading, .title-wrapper h2, .collection__title h2, .title.inline-richtext, h2.title'
    );

    Array.prototype.forEach.call(heads, function (h) {
      if (h.classList.contains('cfx-holo')) return;
      h.classList.add('cfx-holo');
    });

    if (REDUCED) return;

    // Character split only where the heading is plain text (no nested markup),
    // so we never destroy rich-text formatting.
    var hero = document.querySelector('.banner__heading');
    if (!hero || hero.children.length !== 0) return;
    var text = hero.textContent;
    if (!text || text.length > 90) return;

    // the parent must stop clipping its own background once we split it
    hero.classList.remove('cfx-holo');
    hero.classList.add('cfx-headline');
    hero.textContent = '';

    // Split per word first, then per character inside each word. Characters are
    // inline-block, so without the word wrapper a line can break mid-word.
    var words = text.split(/(\s+)/);
    var n = 0;
    words.forEach(function (word) {
      if (!word) return;
      if (/^\s+$/.test(word)) { hero.appendChild(document.createTextNode(word)); return; }
      var w = document.createElement('span');
      w.className = 'cfx-word';
      for (var i = 0; i < word.length; i++) {
        var span = document.createElement('span');
        span.className = 'cfx-char';
        span.style.setProperty('--cfx-cd', (n * 34) + 'ms');
        span.textContent = word[i];
        w.appendChild(span);
        n++;
      }
      hero.appendChild(w);
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { en.target.classList.add('cfx-lit'); obs.unobserve(en.target); }
        });
      }, { threshold: 0.15 });
      io.observe(hero);
    } else {
      hero.classList.add('cfx-lit');
    }
  }

  /* =========================================================================
     8. MAGNETIC BUTTONS
     ========================================================================= */
  function initButtons() {
    var btns = document.querySelectorAll('.button, .quick-add__submit');
    Array.prototype.forEach.call(btns, function (b) {
      if (b.dataset.cfxBtn) return;
      b.dataset.cfxBtn = '1';

      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width;
        var py = (e.clientY - r.top) / r.height;
        b.style.setProperty('--cfx-mx', (px * 100).toFixed(1) + '%');
        b.style.setProperty('--cfx-my', (py * 100).toFixed(1) + '%');
        if (FINE_POINTER && !REDUCED) {
          b.style.setProperty('--cfx-tx', ((px - 0.5) * 10).toFixed(1) + 'px');
          b.style.setProperty('--cfx-ty', ((py - 0.5) * 6).toFixed(1) + 'px');
        }
      }, { passive: true });

      b.addEventListener('pointerleave', function () {
        b.style.setProperty('--cfx-tx', '0px');
        b.style.setProperty('--cfx-ty', '0px');
      });
    });
  }

  /* =========================================================================
     9. SCROLL REVEAL (3D). Skips elements the stock theme already animates
     so the two systems never fight over opacity.
     ========================================================================= */
  function initReveal() {
    if (REDUCED || !('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll(
      '.grid__item, .title-wrapper, .collection__view-all, .multicolumn-card, .collapsible-content__grid'
    );
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); obs.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });

    var groupIndex = 0, lastParent = null;
    Array.prototype.forEach.call(targets, function (t) {
      if (t.classList.contains('cfx-reveal')) return;
      if (t.closest('.scroll-trigger--offscreen')) return;
      if (t.parentElement !== lastParent) { lastParent = t.parentElement; groupIndex = 0; }
      t.style.setProperty('--cfx-delay', Math.min(groupIndex * 80, 480) + 'ms');
      groupIndex++;
      t.classList.add('cfx-reveal');
      io.observe(t);
    });
  }

  /* =========================================================================
     10. PAGE LOAD CURTAIN
     ========================================================================= */
  function initCurtain() {
    if (REDUCED || DESIGN_MODE) return;
    if (sessionStorage) {
      try {
        if (sessionStorage.getItem('cfxSeen')) return;
        sessionStorage.setItem('cfxSeen', '1');
      } catch (e) { /* private mode — just show it */ }
    }
    var c = el('div', 'cfx-curtain');
    c.setAttribute('aria-hidden', 'true');
    setTimeout(function () { if (c.parentNode) c.parentNode.removeChild(c); }, 1200);
  }

  /* =========================================================================
     11. HERO IMAGE — show the whole photo and give it motion.
     Dawn crops it twice (object-fit:cover + .animate--ambient scale(1.2)).
     Wrapping the <img> in a shrink-wrapped frame fixes the fit AND detaches
     it from Dawn's `.animate--ambient > img` selector, retiring that
     animation in favour of ours.
     ========================================================================= */
  function initHero() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    // Only the page's FIRST section can be the hero. Without this, a page whose
    // hero is something else -- the video hero, say -- would hand the two-photo
    // treatment to whatever banner happens to come next down the page.
    var first = main.querySelector(':scope > .shopify-section, :scope > [id^="shopify-section"]');
    if (!first) return;
    var banner = first.querySelector('.banner');
    if (!banner) return;
    banner.classList.add('cfx-hero');

    var media = banner.querySelector('.banner__media');
    if (!media) return;
    var img = media.querySelector(':scope > img');
    if (!img) return;
    if (img.closest('.cfx-hero-frame')) return;

    // Blurred bed: an oversized, heavily blurred copy of the same image fills
    // the banner so the sharp photo never floats in dead space. Same src, so
    // it comes straight from cache -- no extra download.
    if (!media.querySelector(':scope > .cfx-hero-bg')) {
      var bg = document.createElement('div');
      bg.className = 'cfx-hero-bg';
      bg.setAttribute('aria-hidden', 'true');
      var bgImg = img.cloneNode(false);
      bgImg.removeAttribute('id');
      bgImg.setAttribute('alt', '');
      bgImg.setAttribute('aria-hidden', 'true');
      bgImg.removeAttribute('loading');
      bg.appendChild(bgImg);
      media.insertBefore(bg, media.firstChild);
    }

    var frame = document.createElement('div');
    frame.className = 'cfx-hero-frame';
    media.insertBefore(frame, img);

    if (!REDUCED) {
      var halo = document.createElement('div');
      halo.className = 'cfx-hero-halo';
      halo.setAttribute('aria-hidden', 'true');
      frame.appendChild(halo);
    }

    frame.appendChild(img);

    if (!REDUCED) {
      var sweep = document.createElement('div');
      sweep.className = 'cfx-hero-sweep';
      sweep.setAttribute('aria-hidden', 'true');
      frame.appendChild(sweep);
    }

    /* --- two-photo hero ---------------------------------------------------
       Photo A fills the frame; photo B rides in front of it as a square inset
       on its own depth plane (counter-parallax + opposite float phase), so the
       pair reads as two planes rather than one flat collage.
       Both are theme assets pulled in from claude-fx.css, which means no CDN
       URL has to be hard-coded here — the stylesheet resolves them relative to
       its own location in /assets. If either file is missing the layers simply
       render empty and the stock banner still works. */
    frame.classList.add('cfx-hero-duo');
    media.classList.add('cfx-hero-duo-media');

    var shot = document.createElement('div');
    shot.className = 'cfx-hero-shot';
    shot.setAttribute('role', 'img');
    shot.setAttribute('aria-label', img.getAttribute('alt') || 'PulseTech VR headset');
    frame.insertBefore(shot, img);

    var inset = document.createElement('div');
    inset.className = 'cfx-hero-inset';
    inset.setAttribute('role', 'img');
    inset.setAttribute('aria-label', 'PulseTech wireless headphones');
    var insetSheen = document.createElement('span');
    insetSheen.className = 'cfx-hero-inset__sheen';
    insetSheen.setAttribute('aria-hidden', 'true');
    inset.appendChild(insetSheen);
    frame.appendChild(inset);

    // stagger the entrance so B lands just after A has settled
    requestAnimationFrame(function () {
      frame.classList.add('is-lit');
      setTimeout(function () { inset.classList.add('is-in'); }, 380);
    });

    // Size the frame to the picture's fitted box. Doing this in JS keeps the
    // frame exactly congruent with the visible photo at any viewport, which is
    // what the halo, sweep and edge-feather all align to.
    function fitHero() {
      var mw = media.clientWidth;
      var mh = media.clientHeight;
      if (!mw || !mh) return;
      // duo mode ignores the theme image's own ratio — the frame is sized to
      // photo A (1755x1170), which is what actually fills it.
      var ar = frame.classList.contains('cfx-hero-duo')
        ? 1755 / 1170
        : ((img.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : 1.5);
      var w = mw, h = mw / ar;
      if (h > mh) { h = mh; w = mh * ar; }
      frame.style.width = Math.round(w) + 'px';
      frame.style.height = Math.round(h) + 'px';
    }

    fitHero();
    if (img.complete && img.naturalWidth) fitHero();
    img.addEventListener('load', fitHero);
    window.addEventListener('resize', fitHero, { passive: true });
    if (window.ResizeObserver) {
      try { new ResizeObserver(fitHero).observe(media); } catch (e) {}
    }

    if (REDUCED) return;

    // pointer parallax — gentle drift, plus a slightly stronger counter-drift
    // for the inset companion photo so the pair reads as two depth planes.
    if (FINE_POINTER) {
      banner.addEventListener('pointermove', function (e) {
        var r = banner.getBoundingClientRect();
        var px = (e.clientX - r.left) / r.width - 0.5;
        var py = (e.clientY - r.top) / r.height - 0.5;
        frame.style.setProperty('--cfx-hx', (px * 26).toFixed(1) + 'px');
        frame.style.setProperty('--cfx-hy', (py * 18).toFixed(1) + 'px');
        frame.style.setProperty('--cfx-ix', (-px * 16).toFixed(1) + 'px');
        frame.style.setProperty('--cfx-iy', (-py * 12).toFixed(1) + 'px');
      }, { passive: true });

      banner.addEventListener('pointerleave', function () {
        frame.style.setProperty('--cfx-hx', '0px');
        frame.style.setProperty('--cfx-hy', '0px');
        frame.style.setProperty('--cfx-ix', '0px');
        frame.style.setProperty('--cfx-iy', '0px');
      });
    }

    // gentle scroll parallax
    var ticking = false;
    function onScroll() {
      ticking = false;
      var r = banner.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      frame.style.setProperty('--cfx-hscroll', clamp(-r.top * 0.06, -70, 70).toFixed(1) + 'px');
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(onScroll); }
    }, { passive: true });
    onScroll();
  }

  /* =========================================================================
     BOOT
     ========================================================================= */
  function boot() {
    try { initAurora(); } catch (e) {}
    try { initMotes(); } catch (e) {}
    try { initScrollChrome(); } catch (e) {}
    try { initSections(); } catch (e) {}
    try { initHeadings(); } catch (e) {}
    try { initButtons(); } catch (e) {}
    try { initReveal(); } catch (e) {}
    try { initHero(); } catch (e) {}
    try { initCurtain(); } catch (e) {}
  }

  // Re-runnable pieces, for theme-editor section reloads and AJAX cart updates.
  function rebind() {
    try { initSections(); } catch (e) {}
    try { initHeadings(); } catch (e) {}
    try { initButtons(); } catch (e) {}
    try { initReveal(); } catch (e) {}
    try { initHero(); } catch (e) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (DESIGN_MODE) {
    document.addEventListener('shopify:section:load', rebind);
    document.addEventListener('shopify:section:reorder', rebind);
  }

  window.CFX = { rebind: rebind };
})();
